import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Filter, Layers, CheckCircle2 } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import AppNavPublic from '@/components/layout/AppNavPublic';

// ─────────────────────────────────────────────
// ANIMATION DELAY CONFIG
// ─────────────────────────────────────────────
const LINE_1_DELAY = 0.8;
const LINE_2_DELAY = 1.4;
const CALLOUT_1_DELAY = 0.5;
const CALLOUT_2_DELAY = 0.9;

// ─────────────────────────────────────────────
// MESH GRADIENT BACKGROUND
// ─────────────────────────────────────────────
const MeshGradient = () => (
  <>
    <style>{`
      @keyframes meshShift {
        0%   { background-position: 0% 0%; }
        25%  { background-position: 80% 20%; }
        50%  { background-position: 100% 100%; }
        75%  { background-position: 20% 80%; }
        100% { background-position: 0% 0%; }
      }
      @keyframes orb1 {
        0%,100% { transform: translate(0,0) scale(1); }
        33%     { transform: translate(120px,80px) scale(1.35); }
        66%     { transform: translate(-60px,40px) scale(0.85); }
      }
      @keyframes orb2 {
        0%,100% { transform: translate(0,0) scale(1); }
        33%     { transform: translate(-90px,110px) scale(1.45); }
        66%     { transform: translate(70px,-50px) scale(0.8); }
      }
      @keyframes orb3 {
        0%,100% { transform: translate(0,0) scale(1); }
        33%     { transform: translate(-100px,-70px) scale(1.3); }
        66%     { transform: translate(60px,90px) scale(1.1); }
      }
      @keyframes orb4 {
        0%,100% { transform: translate(0,0) scale(1); }
        50%     { transform: translate(80px,-60px) scale(1.2); }
      }
      @keyframes gradientSweep {
        0%   { background-position: 0% center; }
        100% { background-position: 200% center; }
      }
    `}</style>

    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
      {/* Base gradient */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, #000223 0%, #02075d 45%, #1a1a4e 70%, #c8cfe8 100%)',
        backgroundSize: '300% 300%',
        animation: 'meshShift 18s ease-in-out infinite',
      }} />

      {/* Orb 1 — deep indigo top-left */}
      <div style={{
        position: 'absolute', top: '-10%', left: '-5%',
        width: '60%', height: '60%', borderRadius: '50%',
        filter: 'blur(70px)', animation: 'orb1 22s ease-in-out infinite',
        background: 'radial-gradient(circle, rgba(11,26,142,0.1) 0%, #02042a 45%, #0e0221 70%)',
      }} />

      {/* Orb 2 — royal blue center */}
      <div style={{
        position: 'absolute', top: '20%', left: '20%',
        width: '65%', height: '65%', borderRadius: '50%',
        filter: 'blur(90px)', animation: 'orb2 28s ease-in-out infinite',
        background: 'radial-gradient(circle, #0a166d 0%, rgba(10,20,120,0.3) 40%, transparent 70%)',
      }} />

      {/* Orb 3 — light lavender bottom-right */}
      <div style={{
        position: 'absolute', bottom: '-15%', right: '-5%',
        width: '70%', height: '70%', borderRadius: '50%',
        filter: 'blur(100px)', animation: 'orb3 20s ease-in-out infinite',
        background: 'radial-gradient(circle, rgba(220,225,255,0.45) 0%, rgba(160,180,240,0.3) 35%, transparent 70%)',
      }} />

      {/* Orb 4 — teal accent mid-left */}
      <div style={{
        position: 'absolute', top: '40%', left: '-10%',
        width: '50%', height: '50%', borderRadius: '50%',
        filter: 'blur(80px)', animation: 'orb4 24s ease-in-out infinite',
        background: 'radial-gradient(circle, rgba(3,79,100,0.05) 0%, transparent 70%)',
      }} />

      {/* Orb 5 — periwinkle mid-right */}
      <div style={{
        position: 'absolute', top: '25%', right: '0%',
        width: '40%', height: '40%', borderRadius: '50%',
        filter: 'blur(70px)', animation: 'orb2 26s ease-in-out infinite reverse',
        background: 'radial-gradient(circle, rgba(120,140,255,0.3) 0%, transparent 70%)',
      }} />

      {/* Noise grain */}
      <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        backgroundSize: '200px 200px',
      }} />
    </div>
  </>
);

// ─────────────────────────────────────────────
// CALLOUT CARD
// ─────────────────────────────────────────────
interface CalloutCardProps {
  icon: React.ElementType;
  title: string;
  description: string;
  floatDirection: 'up' | 'down';
  delay: number;
}

const CalloutCard = ({ icon: Icon, title, description, floatDirection, delay }: CalloutCardProps) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.92 }}
    animate={{
      opacity: 1,
      scale: 1,
      y: floatDirection === 'up' ? [0, -7, 0] : [0, 7, 0],
    }}
    transition={{
      opacity: { delay, duration: 0.6, ease: 'easeOut' },
      scale:   { delay, duration: 0.6, ease: 'easeOut' },
      y: {
        delay: delay + 0.6,
        duration: floatDirection === 'up' ? 4 : 4.5,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    }}
    whileHover={{
      scale: 1.05,
      boxShadow: '0 32px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(51,153,153,0.3)',
    }}
    className="flex items-center gap-4 px-5 py-3.5 rounded-2xl cursor-default transition-shadow duration-300"
    style={{
      background: 'rgba(255,255,255,0.08)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      border: '1px solid rgba(255,255,255,0.15)',
      boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
      minWidth: '280px',
      maxWidth: '320px',
    }}
  >
    <div className="flex-shrink-0 p-2.5 rounded-xl" style={{
      background: 'rgba(51,153,153,0.2)',
      border: '1px solid rgba(51,153,153,0.3)',
    }}>
      <Icon className="w-5 h-5" style={{ color: '#339999' }} />
    </div>

    <div className="flex flex-col gap-0.5 flex-1">
      <h4 className="text-sm font-extrabold leading-tight tracking-tight" style={{ color: '#00022e' }}>
        {title}
      </h4>
      <p className="text-xs leading-snug" style={{ color: '#64748a', maxWidth: '180px' }}>
        {description}
      </p>
    </div>

    <div className="flex-shrink-0 ml-auto">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
          style={{ backgroundColor: '#339999' }} />
        <span className="relative inline-flex rounded-full h-2 w-2"
          style={{ backgroundColor: '#339999' }} />
      </span>
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────
// ANIMATED SVG CONNECTOR LINES
//
// viewBox="0 0 600 500" maps proportionally onto
// the screenshot via preserveAspectRatio="none".
// strokeWidth="1.5" and r="4" are now visible.
//
// To nudge line endpoints after testing in Lovable,
// edit the path d="M x1 y1 Q cx cy x2 y2" values:
//   Line 1 endpoint (228,315): filter/status row
//   Line 2 endpoint (432,425): Download PDF button
// ─────────────────────────────────────────────
const ConnectorLines = () => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none"
    style={{ zIndex: 20 }}
    viewBox="0 0 600 500"
    preserveAspectRatio="none"
    overflow="visible"
  >
    {/* ── Line 1: Filter by status ── */}
    <motion.path
      d="M 132 235 Q 132 290 228 315"
      fill="none"
      stroke="#339999"
      strokeWidth="1.5"
      strokeLinecap="round"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 1 }}
      transition={{
        pathLength: { delay: LINE_1_DELAY, duration: 0.7, ease: 'easeInOut' },
        opacity:    { delay: LINE_1_DELAY, duration: 0.1 },
      }}
    />
    <motion.circle cx="228" cy="315" r="4" fill="#339999"
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: LINE_1_DELAY + 0.65, duration: 0.25, ease: 'backOut' }}
    />
    <motion.circle cx="228" cy="315" r="4" fill="none"
      stroke="#339999" strokeWidth="1.5"
      initial={{ opacity: 0.6, scale: 1 }}
      animate={{ opacity: 0, scale: 3 }}
      transition={{
        delay: LINE_1_DELAY + 0.65, duration: 1.2,
        repeat: Infinity, repeatDelay: 1.5, ease: 'easeOut',
      }}
    />

    {/* ── Line 2: Generate audit package ── */}
    <motion.path
      d="M 384 340 Q 384 390 432 425"
      fill="none"
      stroke="#339999"
      strokeWidth="1.5"
      strokeLinecap="round"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: 1 }}
      transition={{
        pathLength: { delay: LINE_2_DELAY, duration: 0.7, ease: 'easeInOut' },
        opacity:    { delay: LINE_2_DELAY, duration: 0.1 },
      }}
    />
    <motion.circle cx="432" cy="425" r="4" fill="#339999"
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: LINE_2_DELAY + 0.65, duration: 0.25, ease: 'backOut' }}
    />
    <motion.circle cx="432" cy="425" r="4" fill="none"
      stroke="#339999" strokeWidth="1.5"
      initial={{ opacity: 0.6, scale: 1 }}
      animate={{ opacity: 0, scale: 3 }}
      transition={{
        delay: LINE_2_DELAY + 0.65, duration: 1.2,
        repeat: Infinity, repeatDelay: 1.5, ease: 'easeOut',
      }}
    />
  </svg>
);

// ─────────────────────────────────────────────
// SCREENSHOT + CALLOUT OVERLAY
// ─────────────────────────────────────────────
const ScreenshotWithCallouts = () => (
  <div className="relative w-full h-full flex items-center justify-center">

    <div className="relative w-full" style={{ maxWidth: '680px' }}>

      {/* Radial glow behind image */}
      <motion.div
        className="absolute pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
        style={{
          width: '180%',
          height: '180%',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'radial-gradient(circle, rgba(56,189,248,0.20) 0%, rgba(56,189,248,0.12) 30%, rgba(56,189,248,0.05) 55%, transparent 70%)',
          filter: 'blur(80px)',
          zIndex: -1,
        }}
      />

      {/* Shadow glow */}
      <div className="absolute inset-0 rounded-2xl -z-10 translate-y-4 blur-2xl scale-95 opacity-70" style={{
        background: 'linear-gradient(135deg, #000223 0%, #02075d 60%, #339999 100%)',
      }} />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <img
          src="/assets/veracord-dashboard.png"
          alt="Veracord Audit Export Dashboard"
          className="relative w-full h-auto rounded-2xl"
          style={{
            border: '1px solid rgba(255,255,255,0.1)',
            boxShadow: '-12px 24px 48px -12px rgba(0,0,0,0.4)',
            display: 'block',
          }}
        />
      </motion.div>

      {/* Connector lines — desktop only */}
      <div className="hidden lg:block absolute inset-0 w-full h-full">
        <ConnectorLines />
      </div>
    </div>

    {/* Callout 1 — Filter by status — desktop only */}
    <div className="absolute hidden lg:block" style={{ top: '38%', left: '-5%', zIndex: 30 }}>
      <CalloutCard
        icon={Filter}
        title="Filter by status"
        description="View only overdue policies instantly."
        floatDirection="up"
        delay={CALLOUT_1_DELAY}
      />
    </div>

    {/* Callout 2 — Generate audit package — desktop only */}
    <div className="absolute hidden lg:block" style={{ top: '58%', right: '-8%', zIndex: 30 }}>
      <CalloutCard
        icon={Layers}
        title="Generate audit package"
        description="Export complete, timestamped record in seconds."
        floatDirection="down"
        delay={CALLOUT_2_DELAY}
      />
    </div>

  </div>
);

// ─────────────────────────────────────────────
// MAIN HERO SECTION
// ─────────────────────────────────────────────
export const HeroSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('scrollTo') === 'how-it-works') {
      const timeout = setTimeout(() => {
        document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
        searchParams.delete('scrollTo');
        setSearchParams(searchParams, { replace: true });
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [searchParams, setSearchParams]);

  return (
    <div
      className="relative min-h-[82vh] w-full flex flex-col text-white font-sans"
      style={{ background: 'transparent', isolation: 'isolate' }}
    >
      <MeshGradient />

      {/* ── Navigation ── */}
      <AppNavPublic />

      {/* ── Hero Content ── */}
      <main
        className="flex-1 min-h-0 w-full max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col lg:flex-row items-center gap-16"
        style={{ position: 'relative', zIndex: 10 }}
      >
        {/* Left: Text */}
        <div className="flex flex-col gap-8 lg:w-[45%] flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1
              className="text-5xl md:text-6xl xl:text-7xl font-semibold leading-[1.1] tracking-tight text-white"
              style={{ fontFamily: 'Source Serif Pro, Georgia, serif', letterSpacing: '-0.02em' }}
            >
              Audit-Ready in
              <br />
              <span style={{
                fontSize: '110%',
                background: 'linear-gradient(90deg, #339999 0%, #ffffff 40%, #339999 80%)',
                backgroundSize: '200% auto',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'gradientSweep 3.5s linear infinite',
              }}>
                30 Seconds
              </span>
              <br />
              <span style={{ opacity: 0.8 }}>Not Two Weeks.</span>
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl leading-relaxed max-w-md"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            Veracord is the policy compliance system that turns surprise inspections into a non-event.
          </motion.p>

          <p className="text-xs tracking-wide mt-3 text-white/50">
            AAAASF · Joint Commission · State Licensing · OSHA · HIPAA
          </p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col gap-5"
          >
            <Link
              to="/get-started"
              className="w-fit flex items-center gap-3 px-8 py-4 text-lg font-bold text-white rounded-xl transition-all mb-4"
              style={{
                backgroundColor: '#339999',
                boxShadow: '0 0 30px rgba(51,153,153,0.4)',
              }}
            >
              Get Started
              <motion.span
                animate={{ x: [0, 6, 0] }}
                transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                className="inline-flex"
              >
                <ArrowRight className="w-5 h-5" />
              </motion.span>
            </Link>

            <p className="text-xs mb-3" style={{ color: 'rgba(255,255,255,0.45)' }}>
              From $249/month · or $207/mo billed annually
            </p>

            <div className="flex flex-wrap gap-x-6 gap-y-1">
              {['No setup fees', 'Unlimited staff and policies', 'Cancel anytime'].map((item) => (
                <span key={item} className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  ✔ {item}
                </span>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Right: Screenshot + callouts */}
        <div className="relative lg:w-[55%] w-full" style={{ minHeight: '420px' }}>
          <ScreenshotWithCallouts />
        </div>
      </main>
    </div>
  );
};

export default HeroSection;
